import { PubSubService } from '@ohif/core';
import { findSeriesId, getSeriesLabels } from '../clients/orthanc';
import { describeError, readBulkDataText } from '../utils';

const EVENTS = {
  SERIES_LABELS_CHANGED: 'event::sycai:SeriesLabelService:seriesLabelsChanged',
} as const;

export type SeriesLabelEventPayloads = {
  [EVENTS.SERIES_LABELS_CHANGED]: { seriesInstanceUid: string };
};

export interface SeriesLabels {
  labels: readonly string[];
  reviewerName: string | null;
}

/** The reviewer's initials. M0 measured `4a 47 52 00` -- "JGR\0" -- in this tag. */
const REVIEWER_TAG = '00111010';

function toSequence(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is Record<string, unknown> => !!item && typeof item === 'object'
    );
  }

  // dcmjs's naturalization can hand back a single-item sequence UNWRAPPED. Upstream copes
  // with the same shape at cornerstone-dicom-rt/src/getSopClassHandlerModule.ts:78-80, which
  // reads `ReferencedFrameOfReferenceSequence?.RTReferencedStudySequence` off the sequence
  // object itself rather than off [0]. develop's walk indexed both levels as arrays and would
  // silently match nothing on that shape.
  return value && typeof value === 'object' ? [value as Record<string, unknown>] : [];
}

function referencesSeries(
  instance: Record<string, unknown>,
  seriesInstanceUid: string
): boolean {
  for (const frameOfReference of toSequence(instance.ReferencedFrameOfReferenceSequence)) {
    for (const study of toSequence(frameOfReference.RTReferencedStudySequence)) {
      for (const series of toSequence(study.RTReferencedSeriesSequence)) {
        if (series.SeriesInstanceUID === seriesInstanceUid) {
          return true;
        }
      }
    }
  }

  // The fallback path develop also had. The M0 fixture takes the first branch: its RTSTRUCT
  // carries ReferencedFrameOfReferenceSequence and NO ReferencedSeriesSequence (measured
  // 2026-08-13), so this branch is unexercised locally and §10 risk 3 covers it not matching.
  for (const series of toSequence(instance.ReferencedSeriesSequence)) {
    if (series.SeriesInstanceUID === seriesInstanceUid) {
      return true;
    }
  }

  return false;
}

/**
 * §5.4. develop's walk (useSeriesOrganReviewsOverlayStore.ts:53-117) ports; its dead
 * duplicate at CustomizableViewportOverlay.tsx:503-570 -- zero callers on every branch --
 * does not.
 *
 * `.instance` is the LAST instance of the RT series, not the first:
 * cornerstone-dicom-rt/src/getSopClassHandlerModule.ts:19-21 sorts and takes
 * `instances[instances.length - 1]` as the most recently created structure set, and :68 is
 * where that lands on the display set. That is the one whose private tag we want.
 */
function findReferencingRtInstance(
  displaySetService: AppTypes.DisplaySetService,
  studyInstanceUid: string,
  seriesInstanceUid: string
): Record<string, unknown> | null {
  const rtDisplaySets = displaySetService
    .getActiveDisplaySets()
    .filter(
      displaySet =>
        displaySet.Modality === 'RTSTRUCT' && displaySet.StudyInstanceUID === studyInstanceUid
    );

  for (const rtDisplaySet of rtDisplaySets) {
    const instance = rtDisplaySet.instance as Record<string, unknown> | undefined;

    if (instance && referencesSeries(instance, seriesInstanceUid)) {
      return instance;
    }
  }

  return null;
}

/**
 * D4. A service, not a zustand store: this is server state behind a request lifecycle, and
 * all 12 upstream stores carry a serialization `type` id (useLutPresentationStore.ts:9) that
 * this data has no use for. M4.1 D2 named this milestone as the case that might flip its
 * no-zustand rule -- a reader in a different React tree -- but pub/sub already spans any
 * number of subtrees, so the condition argues against component-local state rather than for
 * zustand.
 *
 * Not folded into OrganReviewService, by D1's own logic: different host, different lifecycle
 * (read-only versus polled-and-submitted), no shared data.
 */
export class SeriesLabelService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'seriesLabelService',
    create: ({ servicesManager }: { servicesManager: AppTypes.ServicesManager }) =>
      new SeriesLabelService(servicesManager),
  };

  private readonly servicesManager: AppTypes.ServicesManager;

  /**
   * Keyed by seriesInstanceUid ALONE, as develop was -- the study UID is a request parameter
   * there and never part of the key. That is correct: series UIDs are globally unique. What
   * develop lacked was any reason to look at the key it wrote: its store destructured `get`
   * at :49 and never used it, so every call refetched.
   */
  private labels = new Map<string, readonly string[]>();

  /**
   * Plan D-4. SEPARATE from `labels`, and written only on a HIT. A miss is not a result: the
   * RTSTRUCT display set may not exist yet when the first CT viewport renders, and caching
   * `null` would keep the name blank for the rest of the mode. A later mount re-runs the walk
   * -- a synchronous read of loaded metadata, no network -- and the bulk request happens only
   * when it matches.
   */
  private reviewerNames = new Map<string, string>();

  /**
   * §5.3 property 2. N viewports mounting the same series at once issue ONE request.
   * DiagnosisService.ts's refresh() is the in-tree precedent, including why the entry
   * clears in a `.finally()` whose identity is re-checked.
   */
  private inFlight = new Map<string, Promise<void>>();

  /**
   * Bumped on mode exit, never cleared, and captured by every read. Same reason
   * OrganReviewService.ts:305-307 bumps rather than clears: zeroing would let a response from
   * the previous mode entry become current again.
   */
  private generation = 0;

  constructor(servicesManager: AppTypes.ServicesManager) {
    super(EVENTS);
    this.servicesManager = servicesManager;
  }

  public get(seriesInstanceUid: string): SeriesLabels | null {
    const labels = this.labels.get(seriesInstanceUid);

    if (!labels) {
      return null;
    }

    return { labels, reviewerName: this.reviewerNames.get(seriesInstanceUid) ?? null };
  }

  /**
   * Never rejects for any error it originates. The hook calls it as `void request(...)`
   * from an effect, so a rejection here would be an unhandled rejection on every series
   * change. A throwing subscriber can still reject it through `_broadcastEvent`
   * (pubSubServiceInterface.ts:83-95 invokes listeners inline, no try/catch) — confirmed
   * unreachable today, since SERIES_LABELS_CHANGED's only subscriber (useSeriesLabels.ts)
   * does a UID comparison and a `setState`, but that is a property of the subscriber, not
   * a guarantee this method makes.
   */
  public async request(studyInstanceUid: string, seriesInstanceUid: string): Promise<void> {
    if (this.labels.has(seriesInstanceUid) && this.reviewerNames.has(seriesInstanceUid)) {
      return;
    }

    const inFlight = this.inFlight.get(seriesInstanceUid);

    if (inFlight) {
      return inFlight;
    }

    const request = this.read(studyInstanceUid, seriesInstanceUid, this.generation).finally(() => {
      // Only if the entry is still this request's: onModeExit clears the map, and deleting
      // someone else's entry would let the next caller issue a duplicate against a live
      // request.
      if (this.inFlight.get(seriesInstanceUid) === request) {
        this.inFlight.delete(seriesInstanceUid);
      }
    });

    this.inFlight.set(seriesInstanceUid, request);

    return request;
  }

  /**
   * §5.3 property 3. develop's store was a module-level singleton, never reset on study or
   * mode change, living for the whole page session. Called for every service by
   * ExtensionManager.ts:200-206.
   */
  public onModeExit(): void {
    this.generation += 1;
    this.labels.clear();
    this.reviewerNames.clear();
    this.inFlight.clear();
  }

  private async read(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    generation: number
  ): Promise<void> {
    const [labels, reviewerName] = await Promise.all([
      this.labels.has(seriesInstanceUid)
        ? Promise.resolve(undefined)
        : this.fetchLabels(seriesInstanceUid),
      this.reviewerNames.has(seriesInstanceUid)
        ? Promise.resolve(undefined)
        : this.fetchReviewerName(studyInstanceUid, seriesInstanceUid),
    ]);

    // ONE guard, ONE write, and the placement is the point: guarding each write separately
    // lets the first land after onModeExit cleared the maps, leaving an entry from the
    // previous study alive for the next one.
    if (this.generation !== generation) {
      return;
    }

    if (labels) {
      this.labels.set(seriesInstanceUid, labels);
    }

    if (reviewerName) {
      this.reviewerNames.set(seriesInstanceUid, reviewerName);
    }

    if (labels || reviewerName) {
      this._broadcastEvent(EVENTS.SERIES_LABELS_CHANGED, { seriesInstanceUid });
    }
  }

  private async fetchLabels(seriesInstanceUid: string): Promise<readonly string[] | undefined> {
    try {
      const orthancSeriesId = await findSeriesId(seriesInstanceUid);

      // §5.2. A miss returns "no labels" and NOT a request. develop's fetchSeriesLabels never
      // guarded seriesId, so a /tools/find miss produced /series/null/labels -- a guaranteed
      // 404, re-issued on every slice.
      return orthancSeriesId ? await getSeriesLabels(orthancSeriesId) : [];
    } catch (error) {
      // D8/§5.2. develop returned [] from its catch, making "the server 500'd"
      // indistinguishable from "nothing is labelled". Nothing is cached, so the next mount of
      // this series retries; request() runs once per series, never per slice, so this cannot
      // storm.
      console.error(
        `[sycai] reading the labels for series ${seriesInstanceUid} failed ` +
          `${describeError(error)}; the overlay keeps its last state`
      );

      return undefined;
    }
  }

  private async fetchReviewerName(
    studyInstanceUid: string,
    seriesInstanceUid: string
  ): Promise<string | undefined> {
    try {
      // Plan D-12: resolved PER CALL, never captured in the constructor, so this depends on
      // no core-service registration order at preRegistration. Same rule
      // config/roots.ts:98-110 applies to the PACS root, and §5.1 restates it. The cast is
      // index.tsx:295-299's pattern: platform/core declares displaySetService optional and the
      // fork does not modify that file, so the assertion is made once, here, where it is used.
      const displaySetService = this.servicesManager.services
        .displaySetService as AppTypes.DisplaySetService;

      const rtInstance = findReferencingRtInstance(
        displaySetService,
        studyInstanceUid,
        seriesInstanceUid
      );

      if (!rtInstance) {
        return undefined;
      }

      // NOT `(await readBulkDataText(...)) ?? undefined` inline: measured 2026-08-13, that
      // shape crashes @babel/plugin-transform-regenerator at TRANSFORM time with "'??' !==
      // '||'" (emit.js:550 asserts a LogicalExpression's operator is `&&` or `||` before
      // exploding it, and an awaited left operand forces that explosion). Same class of bug
      // as SessionService.ts's optional-catch-binding comment: the `test` babel env only
      // (babel.config.js), dev and production builds are unaffected.
      const reviewerName = await readBulkDataText(rtInstance[REVIEWER_TAG]);

      return reviewerName ?? undefined;
    } catch (error) {
      console.error(
        `[sycai] reading the QA reviewer name for series ${seriesInstanceUid} failed ` +
          `${describeError(error)}; the labels still render`
      );

      return undefined;
    }
  }
}

/** See DiagnosisService.ts:517-542 for why this is a declaration merge and an intersection. */
export interface SeriesLabelService {
  subscribe: (<E extends keyof SeriesLabelEventPayloads>(
    eventName: E,
    callback: (data: SeriesLabelEventPayloads[E]) => void
  ) => { unsubscribe: () => void }) &
    PubSubService['subscribe'];
}
